import * as React from 'react';
import styled from 'styled-components';
import defaultTheme from '../../../constants/theme/default.theme';
import { StatusColor } from '../../../constants/statusColors.constant';
import { useTranslation } from 'react-i18next';
import { HexColorPicker } from "react-colorful";
import './ColorPickerCustom.css';
import BaseModalComponent from '../../Modal/BaseModal.component';
import { useModalManager } from '../../../hooks/ModalManager.hook';

interface ColorPickerModalProps extends React.AllHTMLAttributes<HTMLDivElement> {
  value: StatusColor;
  onChangeColor: (color: StatusColor) => void;
  excludedColors?: Array<StatusColor>;
}

export const ColorPickerModal: React.FC<ColorPickerModalProps> = ({
  onChangeColor,
  value,
  excludedColors = []
}) => {
  const { t, i18n } = useTranslation();
  const { closeModal } = useModalManager();

  const namedColorToHex = React.useCallback((namedColor: string): string => {
    interface SMap { [key: string]: string; }
    const colorObj: SMap = {
      'red': (defaultTheme as any).colors.STATUSES_RED,
      'violet': (defaultTheme as any).colors.STATUSES_VIOLET,
      'green': (defaultTheme as any).colors.STATUSES_GREEN,
      'orange': (defaultTheme as any).colors.STATUSES_ORANGE,
      'blue': (defaultTheme as any).colors.STATUSES_BLUE,
      'grey': (defaultTheme as any).colors.STATUSES_GREY,
      'black': (defaultTheme as any).colors.STATUSES_BLACK,
      'white': (defaultTheme as any).colors.STATUSES_WHITE,
    };
    return colorObj[namedColor] || namedColor;
  }, []);

  const handleColorSelection = (color: StatusColor) => {
    onChangeColor(color);
    closeModal();
  };

  return (
    <BaseModalComponent>
      <ColorPickerContainer>
        <section className="custom-pointers">
          <HexColorPicker
            color={namedColorToHex(value)}
            onChange={(v: string) => {
              onChangeColor(v as StatusColor);
            }}
          />
        </section>

        <ColorPickerList>
          {Object.entries(StatusColor)
            .filter(([colorKey, colorValue]) => !colorKey.includes('REGEX'))
            .filter(([colorKey, colorValue]) => !excludedColors.includes(colorValue))
            .map(([colorKey, colorValue]) => (
              <ColorPickerListItem
                key={colorValue}
                onClick={() => handleColorSelection(colorValue)}
              >
                <ColorCube cubeColor={colorValue} />
                <div className={'colorName'}>
                  {t(`statusColor.${colorValue}`)}
                </div>
              </ColorPickerListItem>
            ))}
        </ColorPickerList>
      </ColorPickerContainer>
    </BaseModalComponent>
  );
};

const ColorPickerContainer = styled.div`
  background-color: white;
  border-radius: 5px;
    
  min-width: 256px;
`;

const ColorPickerList = styled.ul`
  display: grid;
  grid-template-rows: repeat(3, 1fr);
  grid-auto-flow: column;
  gap: 10px;

  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const ColorPickerListItem = styled.li`
  padding: 4px 8px;

  display: flex;
  align-items: center;
  gap: 10px;

  font-size: 12px;
  line-height: 16px;

  border: 1px solid ${({ theme }) => theme.colors.BG_SPLASH};
  border-radius: 5px;
  text-transform: capitalize;

  cursor: pointer;

  &:hover {
    background-color: #f1f1f1;
  }
`;

const ColorCube = styled.div<{ cubeColor: StatusColor }>`
  width: 16px;
  height: 16px;
  border-radius: 5px;
  background: ${props => props.cubeColor};
`;
